import facefableLogo from '@/assets/facefable-logo.png';
import onboardingHero from '@/assets/onboarding-hero.webp';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface OnboardingSlideProps {
  onComplete: () => void;
  isCompleting: boolean;
  isLastSlide: boolean;
  onNext?: () => void;
  onGenderSelect?: (gender: string) => void;
}

export const OnboardingSlide1 = ({ onNext }: OnboardingSlideProps) => {
  return (
    <Card className="shadow-card border-0 bg-gradient-card">
      <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center">
      {/* Logo */}
      <div className="mb-4 relative">
        <div className="relative z-10 animate-logo-fade-in">
          <img 
            src={facefableLogo} 
            alt="FaceFable™" 
            className="h-16 md:h-20 object-contain"
          />
        </div>
      </div>

      {/* Hero Image */}
      <div className="mb-6 animate-fade-in">
        <img 
          src={onboardingHero} 
          alt="Skincare Journey" 
          className="h-32 md:h-40 object-cover rounded-lg"
        />
      </div>

      {/* Title */}
      <h1 className="text-xl md:text-2xl font-semibold mb-3 animate-fade-in">
        Welcome to FaceFable™
      </h1>

      {/* Tagline */}
      <p className="text-base font-semibold mb-4 max-w-md mx-auto animate-fade-in" style={{ animationDelay: '0.1s' }}>
        Every face has a story — let's finally make sense of yours.
      </p>

      {/* Body Text */}
      <div className="text-xs text-muted-foreground max-w-md mx-auto space-y-3 animate-fade-in" style={{ animationDelay: '0.2s' }}>
        <p>
          Skincare today is noisy, confusing, and full of guesswork. Too many products, too much conflicting advice, and no way to see what's actually working.
        </p>
        <p className="font-semibold text-foreground">
          FaceFable exists to fix that.
        </p>
        <p>
          We combine AI analysis, dermatology-based science, and your real-world habits to give you clarity, direction, and measurable skin progress — all in one simple system.
        </p>
      </div>

      {/* Continue Button */}
      <Button 
        size="lg" 
        className="w-full max-w-md mt-6 bg-gradient-primary hover:opacity-90 shadow-button"
        onClick={onNext}
      >
        Continue
      </Button>

      {/* Animation Styles */}
      <style>{`
        @keyframes glow-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.4;
          }
          50% {
            transform: scale(1.15);
            opacity: 0.1;
          }
        }

        @keyframes logo-fade-in {
          0% {
            opacity: 0;
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes center-glow {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.2);
          }
        }

        .animate-glow-pulse {
          animation: glow-pulse 3s ease-in-out infinite;
        }

        .animate-logo-fade-in {
          animation: logo-fade-in 1s ease-out;
        }

        .animate-center-glow {
          animation: center-glow 3s ease-in-out infinite;
        }

        .drop-shadow-glow {
          filter: drop-shadow(0 0 20px hsl(var(--primary) / 0.3));
        }

        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
      </CardContent>
    </Card>
  );
};
